/**
 * Two badges, two axes, and no component here that can render one without the other.
 *
 * `web/shapes/devices.md` §6. A device carries a **lifecycle** verdict
 * (`wp_devices.status`, nine values, moved by an administrator or by the
 * `mark_offline` sweep) and a **clock** verdict (`liveness`, three values, computed
 * on every read and never stored). `openapi.yaml` says they can disagree in the same
 * response body and that this "is the truth about both".
 *
 * The temptation on a fleet screen is one coloured dot. This file exists so that the
 * dot cannot be written: `StatusBadge` and `LivenessBadge` are separate exports with
 * separate inputs, and neither takes the other's value.
 *
 * Both go through `Badge`, so each is icon **and** text with colour as the third
 * channel — `DESIGN.md` §4, which makes the label mandatory precisely so a greyscale
 * screenshot still distinguishes them.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';
import type { DeviceLiveness, DeviceStatus } from '../../api/types';
import type { FleetReading } from '../../devices/fleet';
import { Badge } from '../../states/Badge';
import type { IconName, Severity } from '../../states/icons';

/**
 * Exhaustive `Record`s rather than template-built keys, for the reason
 * `CollectionRules` gives about `ActivityCategory`: a tenth lifecycle status added to
 * the wire type without a string to render it must fail to compile, whereas
 * `` `devices.status.${status}` `` would compile and put a raw key on screen.
 */
const STATUS_LABEL: Readonly<Record<DeviceStatus, MessageKey>> = {
  pending: 'devices.status.pending',
  active: 'devices.status.active',
  offline: 'devices.status.offline',
  suspended: 'devices.status.suspended',
  revoked: 'devices.status.revoked',
  uninstalled: 'devices.status.uninstalled',
  agent_outdated: 'devices.status.agent_outdated',
  policy_error: 'devices.status.policy_error',
  sync_error: 'devices.status.sync_error',
};

/**
 * Severity is about **what an administrator has to do**, not about how bad the word
 * sounds. `revoked` and `uninstalled` are settled outcomes somebody chose, so they
 * are `info`; `pending` asks for a decision; the three diagnoses ask for a look.
 */
const STATUS_SEVERITY: Readonly<Record<DeviceStatus, { severity: Severity; icon: IconName }>> = {
  pending: { severity: 'medium', icon: 'hourglass' },
  active: { severity: 'low', icon: 'circle-check' },
  offline: { severity: 'medium', icon: 'clock' },
  suspended: { severity: 'medium', icon: 'pause' },
  revoked: { severity: 'info', icon: 'ban' },
  uninstalled: { severity: 'info', icon: 'square' },
  agent_outdated: { severity: 'medium', icon: 'circle-alert' },
  policy_error: { severity: 'high', icon: 'triangle-alert' },
  sync_error: { severity: 'high', icon: 'triangle-alert' },
};

export function StatusBadge({
  status,
  testId,
}: {
  readonly status: DeviceStatus;
  readonly testId?: string;
}): ReactNode {
  const { t } = useI18n();
  const look = STATUS_SEVERITY[status];
  return (
    <Badge
      severity={look.severity}
      icon={look.icon}
      label={t(STATUS_LABEL[status])}
      title={t('devices.status.help')}
      {...(testId === undefined ? {} : { testId })}
    />
  );
}

/**
 * The four readings — three liveness values and `never_reported`, which is not one.
 *
 * `offline` and `stale` get **different severities and different silhouettes**, which
 * is `AgentOffline`'s own rule one size down: offline is acted on, stale is watched.
 * `never_reported` is separated again because "we have not heard from it since 09:12"
 * and "we have never heard from it" are different sentences, and because
 * `DeviceHealth.offline_minutes` is `null` in the second case rather than a number
 * counted from enrolment.
 */
const READING_LOOK: Readonly<
  Record<FleetReading, { severity: Severity; icon: IconName; key: MessageKey }>
> = {
  online: { severity: 'low', icon: 'circle-check', key: 'devices.reading.online' },
  stale: { severity: 'medium', icon: 'circle-alert', key: 'devices.reading.stale' },
  offline: { severity: 'high', icon: 'triangle-alert', key: 'devices.reading.offline' },
  never_reported: {
    severity: 'high',
    icon: 'hourglass',
    key: 'devices.reading.never_reported',
  },
};

export function LivenessBadge({
  reading,
  testId,
}: {
  readonly reading: FleetReading;
  readonly testId?: string;
}): ReactNode {
  const { t } = useI18n();
  const look = READING_LOOK[reading];
  return (
    <Badge
      severity={look.severity}
      icon={look.icon}
      label={t(look.key)}
      title={
        reading === 'never_reported' ? t('devices.reading.neverHelp') : t('devices.census.help')
      }
      {...(testId === undefined ? {} : { testId })}
    />
  );
}

/** The census row labels, so the strip and the badges cannot drift apart. */
export function readingLabelKey(reading: FleetReading): MessageKey {
  return READING_LOOK[reading].key;
}

/**
 * `DeviceLiveness` narrowed to what `AgentOffline` accepts.
 *
 * `online` is not a value that component takes — "there is no state to render for a
 * device that is fine" — so this returns `null` rather than inventing one, and the
 * caller renders nothing at all.
 */
export function offlineLiveness(liveness: DeviceLiveness): 'stale' | 'offline' | null {
  return liveness === 'online' ? null : liveness;
}
